import { ButtonNavigationBack, ButtonTertiary } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import { FirePerimeterMethod } from "@nova/constants"
import { Perimeter } from "@nova/protobuf"
import classNames from "classnames"
import { RenamableText } from "features/data-management/workspace/TODO-put-in-package/RenamableText"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { selectFirePerimeterById } from "features/entity-caching/fire-perimeters/selectors"
import { selectActivatedFireId } from "features/map/control/selectors"
import { updatePerimeter } from "features/pubsub/entityActions/cubes/perimeter"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import {
    selectRenamingInspectorPanelId,
    setRenamingInspectorPanelId,
} from "panels/content/side-panel/slice.new"
import {
    changeUpdateMethod,
    clearEditingData,
    selectCanConfirmPerimeter,
    selectEditingFirePerimeterId,
    selectUpdateMethod,
} from "plugins/DrawPerimeter/FirePerimeterSlice"
import { PluginSpec } from "plugins/plugin-registry"
import { useCallback, useMemo, useRef } from "react"
import { GenericInspectorPanelConfirmControls } from "../generics/GenericInspectorPanelConfirmControls"
import { initialTitleIsPlaceholder } from "../icons/module"
import { GenericInspectorPanelFooterContainer } from "../inspector-panels/footers/GenericInspectorPanelFooter"
import {
    getGenericInspectorPanelComponent,
    getGenericLayerDataInspectorPanelComponent,
} from "../inspector-panels/GenericInspectorPanel"
import { GenericInspectorPanelHeaderTopRow } from "../inspector-panels/headers/GenericInspectorPanelHeader"
import { InspectorPanelPlugin } from "../useInspectorPanel"
import { InspectorPanelFirePerimeterData } from "./InspectorPanelFirePerimeterData"
import { InspectorPanelFirePerimeterLayer } from "./InspectorPanelFirePerimeterLayer"
import { InspectorPanelFirePerimeterUpdateAuto } from "./update-flow/InspectorPanelFirePerimeterUpdateAuto"
import { InspectorPanelFirePerimeterUpdateManual } from "./update-flow/InspectorPanelFirePerimeterUpdateManual"
import { InspectorPanelFirePerimeterUpdateUpload } from "./update-flow/InspectorPanelFirePerimeterUpdateUpload"
import { geometryDataStore } from "plugins/shared/layers/GeometryDataStore"
import { useNonReactiveCallback } from "@nova/use-non-reactive-callback"
import { useEffectUnsafe } from "@nova/use-effect-unsafe"
import {
    submitPerimeterAuto,
    submitPerimeterUpload,
} from "features/entity-caching/fire-perimeters/thunks"

function getEditingFirePerimeterInspectorPanel(spec: PluginSpec<Perimeter>) {
    const EditingFirePerimeterInspectorPanel = ({
        id,
        plugin,
    }: {
        id: string
        plugin: InspectorPanelPlugin
    }) => {
        const dispatch = useAppDispatch()
        const isCleanedUp = useRef(false)
        const onBlur = useCallback(
            (save = true) => {
                isCleanedUp.current = true
                geometryDataStore.free(save)
                dispatch(clearEditingData())
            },
            [dispatch]
        )
        const onBlurRef = useNonReactiveCallback((save = false) => onBlur(save), [onBlur])
        useEffectUnsafe(() => {
            return () => {
                if (!isCleanedUp.current) {
                    onBlurRef.current(true)
                }
            }
        }, [])

        const InspectorPanelComponent = getGenericInspectorPanelComponent({
            spec,
            Header: EditingPerimeterHeader,
            Body: getEditingPerimeterBody({
                onBlur,
            }),
            Footer: getEditingPerimeterFooter({
                onBlur,
            }),
        })
        return <InspectorPanelComponent id={id} plugin={plugin} />
    }
    return EditingFirePerimeterInspectorPanel
}

function EditingPerimeterHeader() {
    const updateMethod = useAppSelector(selectUpdateMethod())
    const title = useMemo(() => {
        switch (updateMethod) {
            case FirePerimeterMethod.None:
                return "Edit Fire Perimeter"
            case FirePerimeterMethod.Upload:
                return "Upload Fire Perimeter"
            case FirePerimeterMethod.Manual:
                return "Draw Fire Perimeter"
            case FirePerimeterMethod.Auto:
                return "Auto Fire Perimeter"
        }
    }, [updateMethod])
    return (
        <div className="flex flex-col px-4 pt-4 pb-2">
            <GenericInspectorPanelHeaderTopRow typeTitle={title} Icon={Icons.PerimeterPlugin} />
        </div>
    )
}

function getEditingPerimeterBody({ onBlur }: { onBlur: (save?: boolean) => void }) {
    const EditingPerimeterBody = ({ id }: { id: string }) => {
        const dispatch = useAppDispatch()
        const perimeter = useAppSelector(selectFirePerimeterById(id))
        const displayedName = useMemo(
            () =>
                perimeter.name.split(".kml").join("").split(".gpx").join("").split(".srt").join(""),
            [perimeter?.name]
        )

        const renamingInspectorPanelId = useAppSelector(selectRenamingInspectorPanelId)
        const projectId = useAppSelector(selectActivatedFireId)
        const { isEditor } = useWorkspacePermissions(projectId)
        const updateMethod = useAppSelector(selectUpdateMethod())

        const changeEditOption = useCallback(
            (option: FirePerimeterMethod) => {
                dispatch(changeUpdateMethod({ method: option }))
            },
            [dispatch]
        )

        if (updateMethod === FirePerimeterMethod.None) {
            return (
                <div className="border-t px-4 pt-2 border-gray-700 flex flex-col gap-2 my-4">
                    <ButtonNavigationBack
                        title="Back"
                        className="self-start"
                        onClick={() => onBlur(false)}
                    />
                    <h5 className="text-gray-0 select-none">Select a method</h5>
                    <ButtonTertiary
                        key="upload"
                        title="Upload"
                        LeftIcon={Icons.Upload}
                        height="2.5rem"
                        onClick={() => changeEditOption(FirePerimeterMethod.Upload)}
                        clickWillNavigate
                    />
                    <ButtonTertiary
                        key="manual"
                        title="Draw"
                        LeftIcon={Icons.StylusNote}
                        height="2.5rem"
                        onClick={() => changeEditOption(FirePerimeterMethod.Manual)}
                        clickWillNavigate
                    />
                    <ButtonTertiary
                        key="auto"
                        title="Auto"
                        LeftIcon={Icons.GestureSelect}
                        height="2.5rem"
                        onClick={() => changeEditOption(FirePerimeterMethod.Auto)}
                        clickWillNavigate
                    />
                </div>
            )
        }
        return (
            <div className="flex flex-col">
                <div className="p-4">
                    <RenamableText
                        id="inspector-layer-title"
                        Tag="h2"
                        title={displayedName}
                        className={classNames("text-gray-0", {
                            "cursor-text": isEditor,
                        })}
                        isRenaming={renamingInspectorPanelId === perimeter.id}
                        isFullWidth={true}
                        shouldHighlightOnHover={isEditor}
                        shouldMakeTextSelectable={!isEditor}
                        initialTitleIsPlaceholder={initialTitleIsPlaceholder(perimeter.name)}
                        onClick={() => {
                            if (isEditor) {
                                dispatch(setRenamingInspectorPanelId({ id: perimeter.id }))
                            }
                        }}
                        onCancelRenaming={() =>
                            dispatch(setRenamingInspectorPanelId({ id: undefined }))
                        }
                        onConfirmRenaming={(name) => {
                            if (!isEditor) return
                            dispatch(
                                updatePerimeter({
                                    id,
                                    updates: { name },
                                })
                            )
                            dispatch(setRenamingInspectorPanelId({ id: undefined }))
                        }}
                    />
                </div>
                {updateMethod === FirePerimeterMethod.Upload && (
                    <InspectorPanelFirePerimeterUpdateUpload />
                )}
                {updateMethod === FirePerimeterMethod.Manual && (
                    <InspectorPanelFirePerimeterUpdateManual onBlur={onBlur} />
                )}
                {updateMethod === FirePerimeterMethod.Auto && (
                    <InspectorPanelFirePerimeterUpdateAuto />
                )}
            </div>
        )
    }
    return EditingPerimeterBody
}

// Rerenders anytime anything changes, need to fix
function getEditingPerimeterFooter({ onBlur }: { onBlur: (save?: boolean) => void }) {
    const EditingPerimeterFooter = () => {
        const dispatch = useAppDispatch()
        const updateMethod = useAppSelector(selectUpdateMethod())
        const onConfirm = useMemo(() => {
            switch (updateMethod) {
                case FirePerimeterMethod.None:
                    return onBlur // use the same handler for manual and none
                case FirePerimeterMethod.Upload:
                    return () => {
                        dispatch(submitPerimeterUpload())
                        onBlur(false)
                    }
                case FirePerimeterMethod.Manual:
                    return onBlur
                case FirePerimeterMethod.Auto:
                    return async () => {
                        await dispatch(submitPerimeterAuto())
                        onBlur(false)
                    }
            }
        }, [updateMethod, dispatch])
        const canConfirmFromRedux = useAppSelector(selectCanConfirmPerimeter)
        const points = geometryDataStore.useGeometryData("points")
        const canConfirm =
            updateMethod === FirePerimeterMethod.None ? points.length >= 3 : canConfirmFromRedux
        return (
            <GenericInspectorPanelFooterContainer>
                <GenericInspectorPanelConfirmControls
                    onCancel={onBlur}
                    onConfirm={onConfirm}
                    canConfirm={canConfirm}
                />
            </GenericInspectorPanelFooterContainer>
        )
    }
    return EditingPerimeterFooter
}

export function getInspectorPanelFirePerimeter(spec: PluginSpec<Perimeter>) {
    const LayerDataInspectorPanel = getGenericLayerDataInspectorPanelComponent({
        spec,
        LayerComponent: InspectorPanelFirePerimeterLayer,
        DataComponent: InspectorPanelFirePerimeterData,
    })
    const EditingPerimeterInspectorPanel = getEditingFirePerimeterInspectorPanel(spec)
    const InspectorPanelFirePerimeter = ({
        id,
        plugin,
    }: {
        id?: string
        plugin: InspectorPanelPlugin
    }) => {
        const editingPerimeterId = useAppSelector(selectEditingFirePerimeterId())
        if (!id) return null
        if (editingPerimeterId !== id) {
            return <LayerDataInspectorPanel id={id} plugin={plugin} />
        }
        return <EditingPerimeterInspectorPanel id={id} plugin={plugin} />
    }
    return InspectorPanelFirePerimeter
}
